import * as React from 'react'

import useI18n from '@store/i18n/useI18n'
import styled from '@theme/styled-components'

import Loader from 'react-loader-spinner'
import theme from '@theme/Theme'

interface Props {
  csv?: string
  error?: boolean
  outlinederror?: string
  outlinedsuccess?: string
  height?: number
}

const CsvPreview = ({ csv, error, outlinederror, outlinedsuccess, height }: Props) => {
  const i18n = useI18n()

  const [loading, setLoading] = React.useState<boolean>(false)
  const [localError, setLocalError] = React.useState<boolean>(false)

  const [csvData, setCsvData] = React.useState<string[][]>([])

  React.useEffect(() => {
    if (csv) {
      setLoading(true)
      const parseData = csv.split('\n').map((itm) => itm.split(';'))
      if (parseData.length !== 0) {
        setCsvData(parseData[0].map((col, i) => parseData.map((row) => row[i])))
        setLoading(false)
      } else {
        setLocalError(true)
        setLoading(false)
      }
    }
  }, [csv])

  return loading ? (
    <Container>
      <Loader type="ThreeDots" color={theme.colors.mapButtonLoader} height={100} />
    </Container>
  ) : error || localError ? (
    <Container>
      <PrevisualisationError>{i18n.t('components.csvpreview.error')}</PrevisualisationError>
    </Container>
  ) : (
    <CsvContainer height={height}>
      {csvData.map((column, columnIdx) => (
        <TabColumn key={columnIdx}>
          {column.map((row, rowIdx) => (
            <TabRow key={rowIdx} header={rowIdx === 0} odd={rowIdx % 2 === 0}>
              <TabValue
                header={rowIdx === 0}
                odd={rowIdx % 2 === 0}
                onerror={!!outlinederror && !!row && row.indexOf(outlinederror) > -1}
                onsuccess={!!outlinedsuccess && !!row && row.indexOf(outlinedsuccess) > -1}>
                {row}
              </TabValue>
            </TabRow>
          ))}
        </TabColumn>
      ))}
    </CsvContainer>
  )
}
export default CsvPreview

const Container = styled('div')`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`
const PrevisualisationError = styled('div')`
  ${(props) => props.theme.fonts.label};
`
const CsvContainer = styled('div')<{ height?: number }>`
  flex: 1;
  display: flex;
  flex-direction: row;

  margin: 25px;
  border-radius: 10px;
  box-shadow: 0px 0px 5px 1px #2224;
  overflow: scroll;
  ${(props) => props.height && `max-height: ${props.height}px`};

  background: url('/loading.gif') white no-repeat center;
`
const TabColumn = styled('div')`
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: white;
`
const TabRow = styled('div')<{ header: boolean; odd: boolean }>`
  border: 0px solid ${(props) => props.theme.colors.headerBackgroundColor};
  border-right-width: 1px;
  border-bottom-width: 1px;

  display: flex;
  alilgn-items: center;
  justify-content: center;
  flex-direction: column;

  height: 40px;
  max-height: 40px;
  min-height: 40px;

  background-color: ${(props) =>
    props.header
      ? props.theme.colors.headerBackgroundColor
      : props.odd
      ? props.theme.colors.oddBackgroundColor
      : props.theme.colors.tabBackgroundColor};
`
const TabValue = styled('div')<{ header: boolean; odd: boolean; onerror: boolean; onsuccess: boolean }>`
  ${(props) => (props.header ? props.theme.fonts.sectionTitle : props.theme.fonts.label)};
  ${(props) => (props.header ? `font-size: 15px; line-height: 15px;` : `font-size: 13px; line-height: 13px;`)};
  color: ${(props) =>
    props.header
      ? props.theme.colors.headerColor
      : props.onerror
      ? props.theme.colors.tabError
      : props.onsuccess
      ? props.theme.colors.tabSuccess
      : props.odd
      ? props.theme.colors.oddColor
      : props.theme.colors.tabColor};
  overflow: hidden;
`
